<template>
  <div>
    <div class="grid grid-flow-row grid-cols-1 sm:grid-cols-2 gap-4 mt-4 px-2">
      <div class="w-full p-12 bg-white rounded-md">
        <h1 class="text-2xl font-semibold">
          Hello there 👋,
          <span class="font-normal"
            >Please fill the fields to save your new Product!</span
          >
        </h1>
        <hr />
        <form class="mt-6" @submit.prevent="saveOrUpdateProduct">
          <div class="relative">
            <label for="title" class="block text-xl font-semibold text-gray-600"
              >Category</label
            >
            <select
              v-model="product.category_id"
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
            >
              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
                >{{ category.name }}</option
              >
            </select>
            <fa-icon
              class="my-auto text-gray-700 absolute top-0 right-0 mt-10 mr-4 "
              :icon="['fas', 'caret-down']"
            />
          </div>

          <div class="mt-2">
            <label for="title" class="block text-xl font-semibold text-gray-600"
              >Title</label
            >
            <input
              id="title"
              v-model="product.title"
              type="text"
              placeholder="The new product title"
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
            />
          </div>

          <div class="mt-2">
            <label
              for="short_description"
              class="block text-xl font-semibold text-gray-600"
              >Short Description</label
            >
            <input
              id="short_description"
              v-model="product.short_description"
              type="text"
              placeholder="Short product description"
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
            />
          </div>

          <div>
            <label
              for="long_description"
              class="block mt-2 text-xl font-semibold text-gray-600"
              >Long Description</label
            >
            <textarea
              id="long_description"
              v-model="product.long_description"
              rows="4"
              type="text"
              placeholder="Optional description which should tell general details"
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
            />
          </div>

          <div>
            <label
              for="important_note"
              class="block mt-2 text-xl font-semibold text-gray-600"
              >Important Note</label
            >
            <input
              id="important_note"
              v-model="product.important_note"
              type="text"
              placeholder="This is optional. This will pop up as an alert box for the user."
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
            />
          </div>

          <div>
            <label
              for="eur_price"
              class="block mt-2 text-xl font-semibold text-gray-600"
              >Euro Price</label
            >
            <input
              id="eur_price"
              v-model="product.eur_price"
              type="number"
              placeholder="The euro price. US Price will be determined automatically"
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
            />
          </div>

          <button
            type="submit"
            class="text-xl w-full py-3 mt-6 font-medium tracking-widest text-white bg-black shadow-lg focus:outline-none hover:bg-gray-900 hover:shadow-none"
          >
            Speichern
          </button>
        </form>
      </div>
      <div v-if="product.id" class="w-full p-12 bg-white rounded-md">
        <h1 class="text-2xl font-semibold">Now add some product options</h1>
        <hr />
        <template v-for="(option, key) in options">
          <div :key="key">
            <div class="flex items-baseline">
              <span class="w-4/6 text-2xl text-green-600"
                >{{ key + 1 }} Option</span
              >
              <div class="product-options__delete" @click="deleteOption(key)">
                <fa-icon :icon="['fas', 'trash']" size="xs" />
              </div>
              <div
                class="product-options__save"
                @click="createOrUpdateOption(key)"
              >
                <fa-icon :icon="['fas', 'check']" size="xs" />
              </div>
            </div>
            <label
              for="option-name"
              class="mt-2 block text-xl font-semibold text-gray-600"
              >Option-Name (Available colors, sizes...)</label
            >
            <input
              id="option-name"
              type="text"
              placeholder="Available colors"
              class="text-2xl block w-full px-3 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
              v-model="options[key].name"
            />
            <label
              for="option-value"
              class="mt-2 block text-xl font-semibold text-gray-600"
              >Option values with "," seperated</label
            >
            <input
              id="option-value"
              type="text"
              placeholder="Green, Blue, Red"
              class="text-2xl block w-full px-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
              required
              v-model="options[key].options"
            />
            <hr class="w-full bg-green-500 h-1 mt-4" />
          </div>
        </template>
        <div class="w-full mx-auto">
          <div class="product-options__new" @click="newOption">
            <fa-icon :icon="['fas', 'plus']" size="xs" />
          </div>
        </div>
      </div>
    </div>
    <DropZone
      v-if="product && product.id"
      class="mt-4"
      name="productDropZone"
      :options="dropZoneOptions"
    />
  </div>
</template>

<script>
import DropZone from '@/components/ui/portal/Dropzone.vue'
import { allCategories } from '@/services/api/category.js'
import {
  specificProduct,
  updateProduct,
  createProduct,
  createOption,
  deleteOption,
  updateOption
} from '@/services/api/product.js'

import config from '@/config.js'
const apiUrl = config.apiUrl

export default {
  components: {
    DropZone
  },
  data() {
    return {
      token: null,
      categories: [],
      product: {
        category_id: null,
        title: '',
        short_description: '',
        long_description: '',
        important_note: '',
        eur_price: 0
      },
      options: [],
      dropZoneOptions: {
        url: '',
        paramName: 'image',
        thumbnailWidth: 150,
        uploadMultiple: true,
        parallelUploads: 3,
        maxFiles: 12,
        maxFilesize: 8,
        autoProcessQueue: false,
        addRemoveLinks: true,
        dictRemoveFile: 'Remove',
        dictDuplicateFile: 'This image is already in the dropzone!',
        acceptedFiles: 'image/*',
        withCredentials: true
      }
    }
  },
  methods: {
    saveOrUpdateProduct() {
      if (this.product.id) {
        updateProduct(this.product)
      } else {
        createProduct({
          payload: this.product
        }).then(response => {
          this.product = response.product
          this.dropZoneOptions.url = `${apiUrl}product/image/${this.product.id}`
          this.dropZoneOptions.headers = {
            'X-XSRF-TOKEN': this.getToken()
          }
        })
      }
    },
    newOption() {
      this.options.push({
        product_id: this.product.id,
        name: '',
        options: ''
      })
    },
    deleteOption(optionIndex) {
      deleteOption(this.options[optionIndex].id).then(() => {
        this.options.splice(optionIndex, 1)
      })
    },
    createOrUpdateOption(optionIndex) {
      const option = this.options[optionIndex]
      if (!option.id) {
        createOption({ payload: this.options[optionIndex] }).then(response => {
          this.options.splice(optionIndex, 1, response.option)
        })
      } else {
        updateOption(this.options[optionIndex])
      }
    },
    getToken() {
      let name = 'XSRF-TOKEN='
      let decodedCookie = decodeURIComponent(document.cookie)
      let ca = decodedCookie.split(';')
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) == ' ') {
          c = c.substring(1)
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length)
        }
      }
      return ''
    }
  },
  mounted() {
    allCategories().then(response => {
      this.categories = response.categories
    })

    if (this.$route.params.id) {
      specificProduct(this.$route.params.id).then(response => {
        Object.assign(this.product, response.product)
        Object.assign(this.options, response.product.options)
        this.dropZoneOptions.url = `${apiUrl}product/image/${this.product.id}`
        this.dropZoneOptions.headers = {
          'X-XSRF-TOKEN': this.getToken()
        }
      })
    }
  }
}
</script>
<style lang="sass">
.product-options
  &__dropzone-save
    z-index: 5000
    @apply rounded-full bg-green-600 absolute top-0 left-0 ml-2 px-3 mt-2
  &__new
    width: fit-content
    transition: all .1s ease-out
    @apply mt-4 mx-auto bg-gray-700 rounded-md text-white flex justify-center items-center px-2 py-2 cursor-pointer
    &:hover
      transform: scale(1.2)
  &__delete
    width: fit-content
    transition: all .1s ease-out
    @apply mt-4 mx-auto bg-red-700 rounded-md text-white flex justify-center items-center px-2 py-2 cursor-pointer
    &:hover
      transform: scale(1.2)
  &__save
    width: fit-content
    transition: all .1s ease-out
    @apply mt-4 mx-auto bg-green-700 rounded-md text-white flex justify-center items-center px-2 py-2 cursor-pointer
    &:hover
      transform: scale(1.2)
</style>
